import { IBaseAction } from '@models/actions/IBaseAction';
import { ModalSize } from '@srm/ui/types/ModalSize';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import React from 'react';
import { BaseActionExecutor } from './IActionExecutor';
import { IModalMessage } from '@srm/ui/message-modal';
import { isGroupRunActive } from '@services/actions/groupOperationRun';

/** Сервис, отвечающий за проверки доступности действия. Форма ответа у групп своя у каждого вида. */
export interface IAccessibilityService<TGroupResult> {
    checkActionAccessibility: (
        objId: string,
        actionKey: string,
    ) => Promise<{ data: { isAccessibly: boolean; errors?: string[]; failures?: { id: string }[] } }>;
    checkActionGroupAccessibility: (objId: string, actionKey: string) => Promise<{ data: TGroupResult }>;
}

/**
 * Отказ доступности по нескольким объектам: сколько не пройдёт — заголовком окна сообщения, как у остальных
 * отказов, в тексте — причины без повторов.
 */
export const accessibilityFailure = (
    data: { errors?: string[]; failures?: { id: string }[] },
    objId: string,
): { title?: string; errors?: string[] } => {
    const total = objId.split(',').filter((id) => id).length;
    const failed = data.failures?.length ?? 0;
    if (total < 2 || failed === 0) return { errors: data.errors };
    const title = `Действие недоступно для ${failed} из ${total}`;
    return { title, errors: data.errors?.length ? data.errors : [`${title}.`] };
};

/**
 * Каркас запуска действия: проверка доступности, затем групповая проверка при
 * нескольких объектах, затем `runInternal`. Отказ любой из проверок закрывает
 * действие неуспехом, ошибки показываются сообщением из `visualElement`.
 *
 * Наследники дают: как из `objId` получить ид для проверки доступности,
 * откуда взять сервис и что делать с результатом групповой проверки.
 */
export abstract class AccessibilityActionExecutor<TGroupResult> extends BaseActionExecutor {
    protected _errors?: string[];
    /** Заголовок окна отказа (по умолчанию — «Внимание») */
    protected _errorsTitle?: string;
    protected _objId?: string;
    protected _parentId?: string;
    protected _rowData?: any;
    protected _completeHandler?: (isSucceed: boolean) => void;

    /** Ид, с которым спрашивают доступность (у активити — из составного ид) */
    protected abstract resolveAccessibilityId(objId: string): string;

    /** Ид объектов, с которыми дальше работает действие (у документов — без ид родителя линка) */
    protected resolveObjId(objId: string): string {
        return objId;
    }

    protected abstract getService(): Promise<IAccessibilityService<TGroupResult>> | IAccessibilityService<TGroupResult>;

    /** Разбор результата групповой проверки: запустить действие либо запомнить ошибки */
    protected abstract handleGroupAccessibility(
        result: TGroupResult,
        run: (objId: string, action: IBaseAction) => void,
        fail: (errors?: string[]) => void,
        action: IBaseAction,
        objId: string,
    ): void;

    /** Состояние действия, которое наследник кладёт в свои поля до запуска */
    protected abstract storeAction(objId: string, action: IBaseAction): void;

    run = async (
        objId: string,
        parentId: string | undefined,
        action: IBaseAction,
        rowData?: any,
        completeHandler?: (isSucceed: boolean) => void,
        raiseVisualElement?: () => void,
        modalSize?: ModalSize | null,
    ) => {
        this._objId = objId;
        this._parentId = parentId;
        this._rowData = rowData;
        this._completeHandler = completeHandler;
        this.storeAction(objId, action);

        const targetObjId = this.resolveObjId(objId);

        const isMultipleObjects = targetObjId.split(',').filter((item) => item).length > 1;
        const serv = await this.getService();

        const runFor = (runObjId: string, runAction: IBaseAction) => {
            if (this.runInternal) this.runInternal(runObjId, parentId, runAction, rowData, completeHandler, modalSize);
            if (raiseVisualElement) raiseVisualElement();
        };

        const fail = (errors?: string[], title?: string) => {
            this._errors = errors;
            this._errorsTitle = title;
            if (raiseVisualElement) raiseVisualElement();
        };

        const runAction = async () => {
            if (isMultipleObjects && action.hasGroupAccessibility) {
                await serv
                    .checkActionGroupAccessibility(targetObjId, action.key)
                    .then((res) => {
                        this.handleGroupAccessibility(res.data, runFor, fail, action, targetObjId);
                    })
                    .catch(() => {
                        if (completeHandler) completeHandler(false);
                    });
            } else {
                runFor(targetObjId, action);
            }
        };

        // По объектам общую перепроверку не делаем: один ставший недоступным объект отменил бы весь запуск,
        // а сервер перепроверяет каждый объект сам и отдаёт недоступный в итог с причиной.
        if (action.hasAccessibility && !isGroupRunActive(action.key)) {
            await serv
                .checkActionAccessibility(this.resolveAccessibilityId(objId), action.key)
                .then(async (res) => {
                    if (res.data.isAccessibly) {
                        //успешная проверка Accessibility
                        await runAction();
                    } else {
                        const failure = accessibilityFailure(res.data, this.resolveAccessibilityId(objId));
                        fail(failure.errors, failure.title);
                    }
                })
                .catch(() => {
                    if (completeHandler) completeHandler(false);
                });
        } else {
            await runAction();
        }
    };

    abstract runInternal: (
        objId: string,
        parentId: string | undefined,
        action: IBaseAction,
        rowData?: any,
        completeHandler?: (isSucceed: boolean) => void,
        modalSize?: ModalSize | null,
    ) => void;

    visualElement = () => {
        if ((this._errors?.length ?? 0) > 0) {
            sendModalMsg({
                message: this._errors,
                title: this._errorsTitle,
                type: 'error',
                closeBtnClick: () => {
                    if (this._completeHandler) this._completeHandler(false);
                },
            } as IModalMessage);
            return <></>;
        } else {
            return this.visualElementInner ? this.visualElementInner() : <></>;
        }
    };

    visualElementInner?: () => React.ReactElement;
}
